import { yupResolver } from "@hookform/resolvers/yup";
import {
    currentStepIndexSelector,
    insertStepsIfMissing,
    removeStepIfPresent,
    setExistingFunds,
    setNewFunds,
    setWhatToChangeAnswers,
    StepperIndexes,
    useAppDispatch,
    useAppSelector,
    whatToChangeAnswersSelector,
} from "@repo/logic";
import DOMPurify from "dompurify";
import { FC, useEffect, useMemo } from "react";
import { FormProvider, useController, useForm } from "react-hook-form";
import * as Yup from "yup";
import { FormErrorMessage } from "../..";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { SwitchInput } from "../Form";
import { DefaultStepProps } from "../Stepper";
import { BodyTextM, HeadingM } from "../Typography";
import * as Styled from "./PensionQuestionnaireWhatToChange.styled";

type FormValues = {
    existingContribution: boolean | null;
    newContribution: boolean | null;
    atLeastOne: boolean;
};

export const PensionQuestionnaireWhatToChangeInvestmentStrategy: FC<DefaultStepProps> = ({ setContinueCondition }) => {
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();
    const currentPageIndex = useAppSelector(currentStepIndexSelector);
    const savedValues = useAppSelector(whatToChangeAnswersSelector);

    const sanitizedHtmlTexts = useMemo(() => {
        const description = DOMPurify.sanitize(t("investmentStrategy.whatToChange.description"), {
            ALLOWED_TAGS: ["strong"],
        });
        const error = DOMPurify.sanitize(t("investmentStrategy.whatToChange.error"), {
            ALLOWED_TAGS: ["strong"],
        });
        return { description, error };
    }, [t]);

    const schema = Yup.object().shape({
        existingContribution: Yup.boolean().required().nullable(),
        newContribution: Yup.boolean().required().nullable(),
        //one of them must be true
        atLeastOne: Yup.boolean()
            .required()
            .test({
                name: "atLeastOne",
                message: sanitizedHtmlTexts.error,
                test: function (_, ctx) {
                    const { existingContribution, newContribution } = ctx.parent;
                    return Boolean(existingContribution) || Boolean(newContribution);
                },
            }),
    });

    const methods = useForm<FormValues>({
        defaultValues: {
            existingContribution: null,
            newContribution: null,
            atLeastOne: false,
        },
        mode: "onChange",
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        // set saved values if any
        if (savedValues && savedValues.existingContribution === true) {
            methods.setValue("existingContribution", savedValues.existingContribution);
        }
        if (savedValues && savedValues.newContribution === true) {
            methods.setValue("newContribution", savedValues.newContribution);
        }
    }, [methods, savedValues]);

    const {
        trigger,
        watch,
        formState: { isDirty },
        getValues,
    } = methods;

    useEffect(() => {
        // set validation condition for the stepper to be able to continue
        setContinueCondition(async () => {
            const isValid = await trigger();
            if (isValid) {
                //save to redux
                dispatch(
                    setWhatToChangeAnswers({
                        existingContribution: getValues("existingContribution") ?? false,
                        newContribution: getValues("newContribution") ?? false,
                    }),
                );
                // clear funds if the user has deselected the options
                if (getValues("existingContribution") !== true) {
                    dispatch(setExistingFunds({ existingFundRows: undefined }));
                }
                if (getValues("newContribution") !== true) {
                    dispatch(setNewFunds({ newFundRows: undefined }));
                }
            }
            return isValid;
        });
    }, [dispatch, getValues, setContinueCondition, trigger]);

    const [existingContribution, newContribution] = watch(["existingContribution", "newContribution"]);

    useEffect(() => {
        // trigger validation of atLeastOne field when any of the watched values change
        if (isDirty) trigger("atLeastOne");
        // add or remove steps based on the selected options
        if (!existingContribution) dispatch(removeStepIfPresent(StepperIndexes.editExistingInvestmentStrategyPage));
        if (!newContribution) dispatch(removeStepIfPresent(StepperIndexes.editNewInvestmentStrategyPage));
        if (existingContribution)
            dispatch(
                insertStepsIfMissing({
                    steps: [StepperIndexes.editExistingInvestmentStrategyPage],
                    index: currentPageIndex + 1,
                }),
            );
        if (newContribution)
            dispatch(
                insertStepsIfMissing({
                    steps: [StepperIndexes.editNewInvestmentStrategyPage],
                    index: currentPageIndex + 1 + (existingContribution ? 1 : 0),
                }),
            );
    }, [trigger, existingContribution, newContribution, dispatch, currentPageIndex, isDirty]);

    return (
        <FormProvider {...methods}>
            <form>
                <Styled.Wrapper>
                    <Styled.Title>{t("investmentStrategy.whatToChange.title")}</Styled.Title>
                    <Styled.Paragraph
                        dangerouslySetInnerHTML={{ __html: sanitizedHtmlTexts.description }}
                    ></Styled.Paragraph>
                    <Styled.WhatToChangeCardRow>
                        <Styled.Card>
                            <HeadingM>{t("investmentStrategy.existingResources.title")}</HeadingM>
                            <BodyTextM>{t("investmentStrategy.existingResources.label")}</BodyTextM>
                            <SwitchInput name="existingContribution" label={t("global.change")} />
                        </Styled.Card>
                        <Styled.Card>
                            <HeadingM>{t("investmentStrategy.newResources.title")}</HeadingM>
                            <BodyTextM>{t("investmentStrategy.newResources.label")}</BodyTextM>
                            <SwitchInput name="newContribution" label={t("global.change")} />
                        </Styled.Card>
                    </Styled.WhatToChangeCardRow>
                    <AlLeastOneError />
                </Styled.Wrapper>
            </form>
        </FormProvider>
    );
};

const AlLeastOneError: FC = () => {
    const { fieldState } = useController<FormValues>({ name: "atLeastOne" });

    if (!fieldState.error) {
        return null;
    }
    return (
        <Styled.AlLeastOneErrorWrapper>
            <FormErrorMessage name="atLeastOne" severity="error">
                <BodyTextM dangerouslySetInnerHTML={{ __html: fieldState.error.message ?? "" }}></BodyTextM>
            </FormErrorMessage>
        </Styled.AlLeastOneErrorWrapper>
    );
};
